import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

/**
 * The dot-dot-dot menu for communities and groups tags
 *
 * @prop resourceName - The name/title of a tag
 * @prop tagId - The id of a tag
 * @prop communityId - The id of a community
 * @prop groupId - The id of a group if group is present
 */

export default class GroupTagMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    resourceName: { type: String },
    tagId: { type: Number },
    communityId: { type: Number },
    groupId: { type: Number },
  };

  /** @internal */
  get instanceId() {
    return this.tagId;
  }

  /** @internal */
  get communityOrGroupUrl() {
    if (this.groupId) {
      return `/communities/${this.communityId}/groups/${this.groupId}`;
    }
    return `/communities/${this.communityId}`;
  }

  /** @internal */
  get tagsUrl() {
    return `${this.communityOrGroupUrl}/file_browser/tags`;
  }

  /** @internal */
  get editUrl() {
    return `${this.tagsUrl}/${this.tagId}/edit`;
  }

  /** @internal */
  get deleteUrl() {
    return `${this.tagsUrl}/${this.tagId}`;
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t('actions.settings'),
      action: this.actionFor('tagExecJSFromResponse', [this.editUrl]),
      icon: 'cog',
      cssClass: 'edit',
    };
  }

  /** @internal */
  get deleteItem() {
    return {
      title: this.t('actions.delete'),
      action: this.actionFor('tagDelete'),
      role: 'destructive',
      icon: 'user-friends',
      cssClass: 'delete text-danger',
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get items() {
    return [this.settingsItem, this.divider, this.deleteItem];
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.resourceName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'bu-group-tag-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  async tagDelete() {
    if (
      !this.commonActions.confirmDialog(this.t('confirm_dialog_msg.default'))
    ) {
      return false;
    }

    try {
      await this.commonActions.delete(this.deleteUrl);
      this.commonActions.visit(this.tagsUrl);
    } catch {
      this.commonActions.displaySnackBarError(this.t('snack.generic_error'));
    }
  }

  tagExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  render() {
    return html`<ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu>`;
  }
}

GroupTagMenu.registerTagName('bu-group-tag-menu');
GroupTagMenu.registerCommonActions('bu-group-tag-menu');

customElements.get('bu-group-tag-menu') ||
  customElements.define('bu-group-tag-menu', GroupTagMenu);
